import { useState, useEffect } from "react";
import { useI18n } from "./lib/i18n.jsx";
import { auth } from "./lib/firebase.js";
import { onAuthStateChanged } from "firebase/auth";
import MarketplaceHome from "./components/MarketplaceHome.jsx";
import TeamDetail from "./components/TeamDetail.jsx";
import MyTeams from "./components/MyTeams.jsx";
import PublishTeam from "./components/PublishTeam.jsx";
import EditTeam from "./components/EditTeam.jsx";
import InstallFlow from "./components/InstallFlow.jsx";
import OAuthCallback from "./components/OAuthCallback.jsx";
import OAuthSetup from "./components/OAuthSetup.jsx";

function getPath() {
  if (typeof window === "undefined") return "/";
  return window.location.pathname;
}

function navigateTo(path) {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new Event("popstate"));
}

export { navigateTo };

export default function App() {
  const { t, lang } = useI18n();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(getPath());

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    const onPop = () => setRoute(getPath());
    window.addEventListener("popstate", onPop);
    return () => {
      unsub();
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  const params = {};
  const parts = route.split("/").filter(Boolean);
  if (parts[0] === "team" && parts[1]) params.slug = parts[1];
  if (parts[0] === "edit" && parts[1]) params.editId = parts[1];
  if (parts[0] === "install" && parts[1]) params.installTeamId = parts[1];
  if (parts[0] === "oauth" && parts[1] === "callback") params.oauthCallback = true;
  if (parts[0] === "oauth" && parts[1] === "setup") params.oauthSetup = true;
  if (parts[0] === "oauth" && parts[1] !== "callback" && parts[1] !== "setup") params.oauth = true;

  const mainRoute = parts[0] || "home";

  function renderContent() {
    if (params.oauthSetup) return <OAuthSetup />;
    if (params.oauthCallback) return <OAuthCallback />;
    if (params.oauth) return <OAuthCallback />;
    if (params.slug && mainRoute === "team") return <TeamDetail slug={params.slug} user={user} onBack={() => navigateTo("/")} />;
    if (params.installTeamId) return <InstallFlow teamId={params.installTeamId} user={user} onBack={() => navigateTo("/")} />;
    if (mainRoute === "my-teams") return <MyTeams user={user} />;
    if (mainRoute === "publish") return <PublishTeam user={user} onBack={() => navigateTo("/my-teams")} />;
    if (params.editId) return <EditTeam teamId={params.editId} user={user} onBack={() => navigateTo("/my-teams")} />;
    return <MarketplaceHome user={user} />;
  }

  return (
    <div className={`marketplace-app lang-${lang}`}>
      <header className="marketplace-header">
        <a href="https://makeyourcrew.com" className="marketplace-logo" target="_blank" rel="noopener noreferrer">
          MakeYourCrew<span className="logo-muted">.marketplace</span>
        </a>
        <nav className="marketplace-nav">
          <a href="/" className={mainRoute === "home" ? "active" : ""}
             onClick={(e) => { e.preventDefault(); navigateTo("/"); }}>
            {t("title")}
          </a>
          {user && (
            <a href="/my-teams" className={mainRoute === "my-teams" ? "active" : ""}
               onClick={(e) => { e.preventDefault(); navigateTo("/my-teams"); }}>
              {t("my_teams")}
            </a>
          )}
        </nav>
        <div className="marketplace-header-right">
          {user ? (
            <div className="user-badge">{user.displayName || user.email}</div>
          ) : loading ? (
            <span className="loading-dots" />
          ) : (
            <button className="btn btn-ghost" onClick={() => navigateTo("/?login=1")}>
              {t("sign_in")}
            </button>
          )}
        </div>
      </header>
      <main className="marketplace-main">
        {renderContent()}
      </main>
    </div>
  );
}
